import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  Rating,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { GreenPanel, SummaryCard, InfoItem, WorkerCard } from './styles';
import { primaryColor } from '../../utils/theme';
import {
  fetchWorkerSuggestions,
  selectWorkerSuggestionsData,
  selectWorkerSuggestionsLoading,
  selectWorkerSuggestionsError,
  selectWorkerSuggestionsTotalWorkers
} from '../../modules/facilityShifts';
import { AppDispatch } from '../../store';
import SecureAvatar from '../common/SecureAvatar';

// --- Types ---
interface FacilityShiftWorkerSuggestionsProps {
  open: boolean;
  onClose: () => void;
  shiftId?: number | null;
  shiftDetails?: {
    date: string;
    role: string;
    count: string;
  } | null;
}

// --- Component ---

const FacilityShiftWorkerSuggestions: React.FC<FacilityShiftWorkerSuggestionsProps> = ({
  open,
  onClose,
  shiftId,
  shiftDetails = {
    date: '17 Oct 2025',
    role: 'PCA',
    count: '1/1',
  },
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const workerSuggestions = useSelector(selectWorkerSuggestionsData);
  const loading = useSelector(selectWorkerSuggestionsLoading);
  const error = useSelector(selectWorkerSuggestionsError);
  const totalWorkers = useSelector(selectWorkerSuggestionsTotalWorkers);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && shiftId) {
      dispatch(fetchWorkerSuggestions(shiftId));
    }
  }, [open, shiftId, dispatch]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || loading || error || workerSuggestions.length <= 3) return;

    let scrollDirection = 1; // 1 for down, -1 for up
    const scrollAmount = 1;
    const scrollInterval = 50; // milliseconds

    const scroll = () => {
      if (scrollContainer) {
        if (scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight) {
          scrollDirection = -1;
        } else if (scrollContainer.scrollTop <= 0) {
          scrollDirection = 1;
        }
        scrollContainer.scrollTop += scrollAmount * scrollDirection;
      }
    };

    const intervalId = setInterval(scroll, scrollInterval);

    return () => clearInterval(intervalId);
  }, [loading, error, workerSuggestions.length]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxWidth: 850,
        },
      }}
    >
      {/* Grid Container */}
      <Grid container sx={{ minHeight: 450 }}>

        {/* LEFT PANEL: SUCCESS MESSAGE */}
        <Grid size={{ xs: 12, md: 5 }}>
          <GreenPanel>
            {/* Success Icon */}
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 36, color: '#fff' }} />
            </Box>

            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Shift Posted Successfully!
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.5, maxWidth: 260 }}>
              All set! Your shift has been posted and is already reaching the right workers. You’ll hear from us once it’s picked up.
            </Typography>

            {/* Shift Details Summary */}
            <SummaryCard>
              <InfoItem>
                <CalendarTodayOutlinedIcon sx={{ fontSize: '16px' }} />
                <span style={{ fontSize: '13px' }}>{shiftDetails?.date}</span>
              </InfoItem>
              <InfoItem>
                <PersonOutlineOutlinedIcon sx={{ fontSize: '18px' }} />
                <span style={{ fontSize: '13px' }}>{shiftDetails?.role} ({shiftDetails?.count})</span>
              </InfoItem>
            </SummaryCard>
          </GreenPanel>
        </Grid>

        {/* RIGHT PANEL: WORKER LIST */}
        <Grid size={{ xs: 12, md: 7 }} sx={{ position: 'relative', bgcolor: '#fff', p: 0 }}>
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#999' }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ p: 4, pt: 6 }}>
            <Typography variant="h6" fontWeight="bold" align="center" gutterBottom>
              {totalWorkers} Skilled Workers Are Available!
            </Typography>

            <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 4, px: 2 }}>
              We've notified workers who match this role. We will update you as soon as someone accepts the shift.
            </Typography>

            {/* Verified Workers Container */}
            <Box ref={scrollContainerRef} sx={{ bgcolor: '#F9F9F9', p: 2, borderRadius: 2, maxHeight: 250, overflowY: 'auto' }}>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1, ml: 1 }}>
                Verified Workers
              </Typography>
              {loading && <CircularProgress />}
              {error && <Typography color="error">{error}</Typography>}
              {workerSuggestions.map((worker) => (
                <WorkerCard key={worker.worker_id}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <SecureAvatar
                      src={worker.user.profile_picture || ''}
                      alt={worker.user.first_name}
                      sx={{ bgcolor: '#e0e0e0' }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {worker.user.first_name} {worker.user.last_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {worker.preferred_roles?.role_name}
                      </Typography>
                    </Box>
                  </Box>
                  <Rating value={worker.average_rating ? parseFloat(worker.average_rating) : 0} readOnly size="small" />
                </WorkerCard>
              ))}
            </Box>

            {/* Done Button */}
            <Box display="flex" justifyContent="center" mt={3}>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  bgcolor: primaryColor,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 6,
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#4a7566' },
                }}
              >
                Done
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default FacilityShiftWorkerSuggestions;